// NewItemDropdown — "+ 새 항목" 단일 버튼 + 12종 타입 드롭다운 (UX v2 polish).
//
// 기존 3종 버튼("크리덴셜 추가" / ".env 추가" / "메모 추가")을 대체한다.
// 클릭 → Radix DropdownMenu → 카테고리별 12종 그룹화.
// 항목 선택 → URL ?new={type} 설정 → NewItemPanel이 신규 작성 모드로 열림.
//
// 키보드: Shift+N 단축키로도 드롭다운 포커스 이동 (useGlobalShortcuts 연동).

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  KeyRound,
  Code2,
  Server,
  FileCode2,
  ShieldCheck,
  HardDrive,
  Globe,
  Layers,
  CreditCard,
  FileText,
  CheckSquare,
  StickyNote,
  ChevronDown,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownItem,
  DropdownSeparator,
  DropdownLabel,
} from '../ui/DropdownMenu';
import { Button } from '../ui/Button';
import type { ItemType } from '../api/items';

// ─── 타입 정의 ───────────────────────────────────────────────

interface TypeEntry {
  type: ItemType;
  label: string;
  icon: React.ReactNode;
}

// ─── 12종 타입 목록 (카테고리별) ─────────────────────────────

/** 크리덴셜 카테고리 */
const CREDENTIAL_TYPES: TypeEntry[] = [
  { type: 'credential_account', label: '계정', icon: <KeyRound size={13} /> },
  { type: 'credential_api_key', label: 'API 키', icon: <Code2 size={13} /> },
  { type: 'credential_server', label: '서버', icon: <Server size={13} /> },
  { type: 'credential_env', label: '환경변수', icon: <FileCode2 size={13} /> },
  { type: 'credential_auth', label: '인증', icon: <ShieldCheck size={13} /> },
];

/** 인프라 카테고리 */
const INFRA_TYPES: TypeEntry[] = [
  { type: 'server', label: '서버 인벤토리', icon: <HardDrive size={13} /> },
  { type: 'domain', label: '도메인', icon: <Globe size={13} /> },
  { type: 'service', label: '서비스', icon: <Layers size={13} /> },
];

/** 자산 카테고리 */
const ASSET_TYPES: TypeEntry[] = [
  { type: 'subscription', label: '구독·라이선스', icon: <CreditCard size={13} /> },
  { type: 'file', label: '파일', icon: <FileText size={13} /> },
  { type: 'checklist', label: '체크리스트', icon: <CheckSquare size={13} /> },
];

/** 기타 카테고리 */
const OTHER_TYPES: TypeEntry[] = [
  { type: 'note', label: '메모', icon: <StickyNote size={13} /> },
];

// ─── 컴포넌트 ─────────────────────────────────────────────────

/**
 * NewItemDropdown — "+ 새 항목 ▼" 단일 primary 버튼 + 12종 드롭다운.
 * 항목 선택 시 URL ?new={type} 설정 → NewItemPanel이 신규 작성 모드로 열린다.
 */
export function NewItemDropdown() {
  const [, setSearchParams] = useSearchParams();

  // 타입 선택 → URL ?new={type} 설정
  const handleSelectType = useCallback(
    (type: ItemType) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('new', type);
          // 기존 item 패널 닫기 (충돌 방지)
          next.delete('item');
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  return (
    <DropdownMenu
      trigger={
        <Button
          variant="primary"
          size="sm"
          endIcon={<ChevronDown size={13} />}
          aria-label="새 항목 추가"
        >
          새 항목
        </Button>
      }
      align="end"
      sideOffset={4}
    >
      {/* ── 크리덴셜 ── */}
      <DropdownLabel>크리덴셜</DropdownLabel>
      {CREDENTIAL_TYPES.map((entry) => (
        <DropdownItem
          key={entry.type}
          icon={entry.icon}
          onSelect={() => handleSelectType(entry.type)}
        >
          {entry.label}
        </DropdownItem>
      ))}

      <DropdownSeparator />

      {/* ── 인프라 ── */}
      <DropdownLabel>인프라</DropdownLabel>
      {INFRA_TYPES.map((entry) => (
        <DropdownItem
          key={entry.type}
          icon={entry.icon}
          onSelect={() => handleSelectType(entry.type)}
        >
          {entry.label}
        </DropdownItem>
      ))}

      <DropdownSeparator />

      {/* ── 자산 ── */}
      <DropdownLabel>자산</DropdownLabel>
      {ASSET_TYPES.map((entry) => (
        <DropdownItem
          key={entry.type}
          icon={entry.icon}
          onSelect={() => handleSelectType(entry.type)}
        >
          {entry.label}
        </DropdownItem>
      ))}

      <DropdownSeparator />

      {/* ── 기타 ── */}
      <DropdownLabel>기타</DropdownLabel>
      {OTHER_TYPES.map((entry) => (
        <DropdownItem
          key={entry.type}
          icon={entry.icon}
          onSelect={() => handleSelectType(entry.type)}
        >
          {entry.label}
        </DropdownItem>
      ))}
    </DropdownMenu>
  );
}
